/**
 * Project Plan Modal (v1 — landing only)
 *
 * An opt-in "Your project plan" modal opened from the landing hero's flag-gated
 * "Project setup" button (see HeroSectionV2 + LandingPageV2). It captures, BEFORE
 * the user clicks Create:
 *
 *   - Always visible (high-frequency): Aspect ratio, Target duration, Language.
 *   - Advanced (collapsible, collapsed by default): the story fields — title,
 *     logline, storyline, tone, structure (free text).
 *
 * Output quality is deliberately NOT offered here (product decision, 2026-10):
 * the plan never sends `video_resolution`, so new projects take the backend
 * create-time default.
 *
 * Simplified layout (designer mock, 2026-10): the default view stays clean and
 * the complex configuration sits behind the "Advanced" disclosure. Advanced
 * opens on load only when it already holds content (`hasAdvancedPlanContent`),
 * so a reopened plan never hides text the user typed. Only an explicit click
 * reports `onAdvancedToggle` (analytics: landing_page_project_setup_advanced_toggled).
 *
 * IA v2 (RFC-1055 follow-up): Orientation MOVES into this modal (out of the
 * inline hero pill) when the flag is on, so the hero toolbar has two clear
 * entries — a dedicated Style button + this "Project setup" button. Orientation
 * is NOT a draft field: it stays owned by the caller's `selectedAspectRatio`
 * state and live-commits through `onAspectRatioChange` (same live-commit
 * semantics the inline pill had — Cancel does NOT roll it back), so there is
 * exactly one owner whether it is surfaced inline (flag off) or here (flag on).
 *
 * Visual style is DELIBERATELY excluded — style stays its own dedicated hero
 * button (StyleLibraryModal): it is the single highest-impact creative dial and
 * a browse/discovery experience, so it earns dedicated visibility. The modal
 * never repeats it. Host-agnostic + fully controlled: the story/video defaults
 * ride a single `ProjectPlanDraft` prop; orientation rides its own controlled
 * pair (so option-B dashboard reuse is a pure mount later).
 *
 * Reuses the shared building blocks (buildContentLanguageOptions, ui/Modal) so it never re-implements a picker.
 * Panel chrome (surface-2 panel, amber primary, Cancel / Save plan footer)
 * matches the restyled StyleLibraryModal. All copy resolves via
 * t('projectPlanV2.*').
 *
 * Feature tag: `landing-project-plan`. NAD trace command:
 *
 *     scripts/nad trace --feature landing-project-plan --last 10m
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '../../lib/utils'
import { createNadDebug } from '../../utils/debugLog'
import Modal from '../ui/Modal'
import type { AspectRatio } from '../landing/hero-content'
import {
  CONTENT_LANGUAGE_AUTO,
  buildContentLanguageOptions,
} from '../../i18n/contentLanguages'

// Client-side caps that mirror the backend request-model limits so the user
// never hits a raw 422 on Create (same discipline as the hero prompt hard-cap).
// title → CreateProjectRequest.name; logline/storyline → CREATIVE_TEXT_MAX_LENGTH;
// tone/structure → 200.
export const PLAN_TITLE_MAX_LENGTH = 100
export const PLAN_CREATIVE_TEXT_MAX_LENGTH = 10_000
export const PLAN_SHORT_TEXT_MAX_LENGTH = 200
export const PLAN_TARGET_DURATION_MIN_S = 1
export const PLAN_TARGET_DURATION_MAX_S = 3600

const log = createNadDebug('landing-project-plan')

// Aspect-ratio options for the modal's toggle buttons. Portrait first, matching
// the designer mock (and DEFAULT_ASPECT_RATIO = 9:16, the pre-selected value).
const ORIENTATION_OPTIONS: ReadonlyArray<{
  value: AspectRatio
  labelKey: string
}> = [
  { value: '9:16', labelKey: 'projectPlanV2.video.orientation.portrait' },
  { value: '16:9', labelKey: 'projectPlanV2.video.orientation.landscape' },
]

/**
 * The net-new story + video-default fields the landing surface captures.
 * `style` is NOT here (its own hero button); `aspectRatio` is NOT here either —
 * it is a controlled prop pair bound to the caller's existing state, live-
 * committed (not part of the roll-back-on-cancel draft). `contentLanguage` uses
 * the 'auto' sentinel in the UI (backend null).
 */
export interface ProjectPlanDraft {
  title: string
  logline: string
  storyline: string
  tone: string
  structure: string
  contentLanguage: string
  targetDurationS: number | null
}

// The pristine draft. `contentLanguage: 'auto'` and `targetDurationS: null` are
// the "unset" sentinels the create payload omits.
export const EMPTY_PROJECT_PLAN_DRAFT: ProjectPlanDraft = {
  title: '',
  logline: '',
  storyline: '',
  tone: '',
  structure: '',
  contentLanguage: CONTENT_LANGUAGE_AUTO,
  targetDurationS: null,
}

/**
 * True when any field that lives under the Advanced disclosure differs from its
 * pristine value (text compared trimmed, like the create payload). Drives the
 * open-on-load rule: a plan with Advanced content never opens collapsed.
 */
export function hasAdvancedPlanContent(draft: ProjectPlanDraft): boolean {
  return (
    draft.title.trim() !== '' ||
    draft.logline.trim() !== '' ||
    draft.storyline.trim() !== '' ||
    draft.tone.trim() !== '' ||
    draft.structure.trim() !== ''
  )
}

// --- Untyped-state parsing (login round-trip). Mirrors the LandingPageV2 /
// LoginPage `stringField` pattern: read fields off an unknown location.state
// object WITHOUT casts (forbidden-TS) so a resumed plan survives /login. ---

function planStringField(value: object, key: string): string {
  const entry = Object.entries(value).find(([entryKey]) => entryKey === key)
  return typeof entry?.[1] === 'string' ? entry[1] : ''
}

function planNumberOrNullField(value: object, key: string): number | null {
  const entry = Object.entries(value).find(([entryKey]) => entryKey === key)
  return typeof entry?.[1] === 'number' && Number.isFinite(entry[1]) ? entry[1] : null
}

/**
 * Parse a `ProjectPlanDraft` out of an unknown `location.state` field. Returns
 * `null` when absent so callers can fall back to the pristine draft. Every field
 * defaults to its pristine value, so a partially-filled plan round-trips intact.
 */
export function projectPlanDraftFromUnknown(
  value: object | null | undefined,
): ProjectPlanDraft | null {
  if (!value || typeof value !== 'object') return null
  const contentLanguage = planStringField(value, 'contentLanguage')
  return {
    title: planStringField(value, 'title'),
    logline: planStringField(value, 'logline'),
    storyline: planStringField(value, 'storyline'),
    tone: planStringField(value, 'tone'),
    structure: planStringField(value, 'structure'),
    contentLanguage: contentLanguage || CONTENT_LANGUAGE_AUTO,
    targetDurationS: planNumberOrNullField(value, 'targetDurationS'),
  }
}

function parseDurationInput(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return null
  const rounded = Math.round(parsed)
  if (rounded < PLAN_TARGET_DURATION_MIN_S) return PLAN_TARGET_DURATION_MIN_S
  if (rounded > PLAN_TARGET_DURATION_MAX_S) return PLAN_TARGET_DURATION_MAX_S
  return rounded
}

export interface ProjectPlanModalProps {
  isOpen: boolean
  onClose: () => void
  draft: ProjectPlanDraft
  onChange: (next: ProjectPlanDraft) => void
  // Orientation is owned by the caller (single owner: LandingPageV2's
  // currentAspectRatio). It is NOT part of `draft` and live-commits — Cancel /
  // Escape / backdrop do NOT roll it back (matches the inline pill + style).
  selectedAspectRatio: AspectRatio
  onAspectRatioChange: (ratio: AspectRatio) => void
  /**
   * Fired only when the user clicks the Advanced disclosure (never for the
   * open-on-load expansion). `hasAdvancedContent` is the state at click time.
   */
  onAdvancedToggle?: (expanded: boolean, hasAdvancedContent: boolean) => void
}

const LABEL_CLASS = 'block text-xs font-medium uppercase tracking-wide text-muted-foreground'

const FIELD_INPUT_CLASS =
  'w-full rounded-lg border border-input-border bg-input-bg px-3 text-base text-foreground ' +
  'placeholder:text-text-muted transition-colors ' +
  'hover:border-input-border-hover focus:outline-none focus:border-primary/60 ' +
  'focus:ring-2 focus:ring-primary/20'
const SINGLE_LINE_INPUT_CLASS = cn(FIELD_INPUT_CLASS, 'h-10')
// Logline / storyline: one line tall like the mock, grows with content.
const GROWING_TEXTAREA_CLASS = cn(
  FIELD_INPUT_CLASS,
  'block min-h-10 max-h-40 resize-none overflow-y-auto py-2 leading-6',
)

const TOGGLE_OPTION_BASE =
  'h-10 whitespace-nowrap rounded-lg border px-4 text-base transition-colors cursor-pointer ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
const TOGGLE_OPTION_CHECKED = 'border-primary bg-primary/15 text-primary'
const TOGGLE_OPTION_UNCHECKED =
  'border-input-border bg-surface-2 text-foreground/85 hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground'

/** Grow a textarea to fit its content (capped by its max-height class). */
function fitTextareaHeight(element: HTMLTextAreaElement | null) {
  if (!element) return
  element.style.height = 'auto'
  element.style.height = `${element.scrollHeight}px`
}

export default function ProjectPlanModal({
  isOpen,
  onClose,
  draft,
  onChange,
  selectedAspectRatio,
  onAspectRatioChange,
  onAdvancedToggle,
}: ProjectPlanModalProps) {
  const { t } = useTranslation('landing')
  const languageOptions = useMemo(() => buildContentLanguageOptions(t), [t])
  const advancedRegionId = useId()
  const [advancedOpen, setAdvancedOpen] = useState(false)

  // Snapshot the draft on the open transition so Cancel/Escape/backdrop can
  // discard live edits (edits apply immediately via onChange). Captured only
  // when isOpen flips false→true — depending on isOpen alone freezes the
  // opening-render values in the closure. Orientation is intentionally NOT
  // snapshotted: it live-commits and is not rolled back (see props).
  // The same transition resets the Advanced disclosure: collapsed by default,
  // open when it already holds content.
  const snapshotRef = useRef<ProjectPlanDraft | null>(null)
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      snapshotRef.current = draft
      wasOpenRef.current = true
      const openOnLoad = hasAdvancedPlanContent(draft)
      setAdvancedOpen(openOnLoad)
      log({ event: 'plan-open', advanced_open_on_load: openOnLoad })
    } else if (!isOpen) {
      wasOpenRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const patch = useCallback(
    (partial: Partial<ProjectPlanDraft>) => {
      onChange({ ...draft, ...partial })
    },
    [draft, onChange],
  )

  const handleCancel = useCallback(() => {
    if (snapshotRef.current) {
      onChange(snapshotRef.current)
    }
    onClose()
  }, [onChange, onClose])

  const handleToggleAdvanced = useCallback(() => {
    const next = !advancedOpen
    const hasContent = hasAdvancedPlanContent(draft)
    setAdvancedOpen(next)
    log({ event: 'advanced-toggle', expanded: next, has_advanced_content: hasContent })
    onAdvancedToggle?.(next, hasContent)
  }, [advancedOpen, draft, onAdvancedToggle])

  const durationValue = draft.targetDurationS === null ? '' : String(draft.targetDurationS)

  // Escape / backdrop / X all discard exploratory draft edits (route through
  // handleCancel), consistent with the Cancel button; only Save commits.
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={t('projectPlanV2.title')}
      size="md"
      padded={false}
      surfaceClassName="bg-surface-2 rounded-xl border border-border shadow-2xl"
    >
      <div data-testid="project-plan-modal">
        {/* Always visible: the high-frequency controls. */}
        <div className="space-y-6 px-8 pt-6 pb-8">
          <div className="flex flex-wrap items-end gap-x-3 gap-y-5">
            {/* Aspect ratio — live-commits via onAspectRatioChange (NOT a draft
                field; not rolled back by Cancel), matching how style lives on
                the hero. */}
            <div className="space-y-2">
              <span className={LABEL_CLASS}>{t('projectPlanV2.video.orientation.label')}</span>
              <div
                role="radiogroup"
                aria-label={t('projectPlanV2.video.orientation.label')}
                className="flex gap-2"
              >
                {ORIENTATION_OPTIONS.map(({ value, labelKey }) => {
                  const checked = selectedAspectRatio === value
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      data-testid={`plan-orientation-${value.replace(':', '-')}`}
                      onClick={() => onAspectRatioChange(value)}
                      className={cn(
                        TOGGLE_OPTION_BASE,
                        checked ? TOGGLE_OPTION_CHECKED : TOGGLE_OPTION_UNCHECKED,
                      )}
                    >
                      {t(labelKey)}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Target duration */}
            <label className="block space-y-2">
              <span className={LABEL_CLASS}>{t('projectPlanV2.video.targetDuration.label')}</span>
              <div className="flex items-center gap-2.5">
                <input
                  data-testid="plan-duration-input"
                  type="number"
                  inputMode="numeric"
                  min={PLAN_TARGET_DURATION_MIN_S}
                  max={PLAN_TARGET_DURATION_MAX_S}
                  value={durationValue}
                  placeholder={t('projectPlanV2.video.targetDuration.placeholder')}
                  onChange={(event) =>
                    patch({ targetDurationS: parseDurationInput(event.target.value) })
                  }
                  className={cn(SINGLE_LINE_INPUT_CLASS, 'w-40')}
                />
                <span className="text-base text-foreground/85">
                  {t('projectPlanV2.video.targetDuration.unit')}
                </span>
              </div>
            </label>
          </div>

          {/* Language */}
          <label className="block space-y-2">
            <span className={LABEL_CLASS}>{t('projectPlanV2.video.language.label')}</span>
            <select
              data-testid="plan-language-select"
              value={draft.contentLanguage}
              onChange={(event) => patch({ contentLanguage: event.target.value })}
              className={cn(SINGLE_LINE_INPUT_CLASS, 'cursor-pointer')}
            >
              {languageOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Advanced disclosure — collapsed by default. */}
        <button
          type="button"
          data-testid="plan-advanced-toggle"
          aria-expanded={advancedOpen}
          aria-controls={advancedRegionId}
          onClick={handleToggleAdvanced}
          className="flex w-full items-center gap-2 border-t border-border px-6 py-2.5 text-left text-sm text-foreground/85 transition-colors hover:bg-surface-3 hover:text-foreground cursor-pointer focus:outline-none focus-visible:bg-surface-3"
        >
          {advancedOpen ? (
            <ChevronUp className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
          )}
          {t('projectPlanV2.advanced.label')}
        </button>

        {advancedOpen && (
          <div
            id={advancedRegionId}
            data-testid="plan-advanced-section"
            className="space-y-6 px-8 pt-3 pb-8"
          >
            <label className="block space-y-2">
              <span className={LABEL_CLASS}>{t('projectPlanV2.story.projectTitle.label')}</span>
              <input
                data-testid="plan-title-input"
                type="text"
                value={draft.title}
                maxLength={PLAN_TITLE_MAX_LENGTH}
                placeholder={t('projectPlanV2.story.projectTitle.placeholder')}
                onChange={(event) => patch({ title: event.target.value })}
                className={SINGLE_LINE_INPUT_CLASS}
              />
            </label>

            <label className="block space-y-2">
              <span className={LABEL_CLASS}>{t('projectPlanV2.story.logline.label')}</span>
              <textarea
                data-testid="plan-logline-input"
                rows={1}
                ref={fitTextareaHeight}
                value={draft.logline}
                maxLength={PLAN_CREATIVE_TEXT_MAX_LENGTH}
                placeholder={t('projectPlanV2.story.logline.placeholder')}
                onChange={(event) => {
                  fitTextareaHeight(event.target)
                  patch({ logline: event.target.value })
                }}
                className={GROWING_TEXTAREA_CLASS}
              />
            </label>

            <label className="block space-y-2">
              <span className={LABEL_CLASS}>{t('projectPlanV2.story.storyline.label')}</span>
              <textarea
                data-testid="plan-storyline-input"
                rows={1}
                ref={fitTextareaHeight}
                value={draft.storyline}
                maxLength={PLAN_CREATIVE_TEXT_MAX_LENGTH}
                placeholder={t('projectPlanV2.story.storyline.placeholder')}
                onChange={(event) => {
                  fitTextareaHeight(event.target)
                  patch({ storyline: event.target.value })
                }}
                className={GROWING_TEXTAREA_CLASS}
              />
            </label>

            <div className="grid gap-x-3 gap-y-6 md:grid-cols-2">
              <label className="block space-y-2">
                <span className={LABEL_CLASS}>{t('projectPlanV2.story.tone.label')}</span>
                <input
                  data-testid="plan-tone-input"
                  type="text"
                  value={draft.tone}
                  maxLength={PLAN_SHORT_TEXT_MAX_LENGTH}
                  placeholder={t('projectPlanV2.story.tone.placeholder')}
                  onChange={(event) => patch({ tone: event.target.value })}
                  className={SINGLE_LINE_INPUT_CLASS}
                />
              </label>

              <label className="block space-y-2">
                <span className={LABEL_CLASS}>{t('projectPlanV2.story.structure.label')}</span>
                <input
                  data-testid="plan-structure-input"
                  type="text"
                  value={draft.structure}
                  maxLength={PLAN_SHORT_TEXT_MAX_LENGTH}
                  placeholder={t('projectPlanV2.story.structure.placeholder')}
                  onChange={(event) => patch({ structure: event.target.value })}
                  className={SINGLE_LINE_INPUT_CLASS}
                />
              </label>
            </div>
          </div>
        )}

        {/* Footer — same Cancel / Save plan pair as the restyled Style Library. */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-8 py-5">
          <button
            type="button"
            data-testid="plan-cancel-button"
            onClick={handleCancel}
            className="h-10 rounded-lg border border-input-border-hover bg-surface-2 px-4 text-base text-foreground transition-colors hover:bg-surface-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 cursor-pointer"
          >
            {t('projectPlanV2.cancel')}
          </button>
          <button
            type="button"
            data-testid="plan-save-button"
            onClick={onClose}
            className="h-10 rounded-lg bg-primary px-4 text-base font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 cursor-pointer"
          >
            {t('projectPlanV2.save')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
